// Legal page content + render helper. Three pages (imprint / privacy / tos)
// share the same scrollable layout and typography; only the content differs.
// Content here is a placeholder — wording is expected to be replaced before
// public launch.

import { VERSION_LABEL } from './version.js';

const BACK_SVG = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6 L9 12 L15 18"/></svg>`;

const LEGAL_CONTENT = {
    imprint: {
        title: 'Legal Imprint',
        eyebrow: 'Legal information',
        lastUpdated: 'Last updated 02 May 2026',
        intro: 'Information pursuant to Section 5 German Telemedia Act (TMG). PuzzleMines.com is a software product operated by aheadware GmbH.',
        sections: [
            { heading: 'Service Provider', kind: 'address', body: [
                'aheadware GmbH',
                'Wandelfeld 78',
                '21407 Deutsch Evern',
                'Germany',
            ]},
            { heading: 'Contact', kind: 'kv', body: [
                [ 'Phone',   '+49-4131-217 40 60' ],
                [ 'Email',   'info@aheadware.com' ],
                [ 'Website', 'aheadware.com' ],
            ]},
            { heading: 'Managing Director', kind: 'paragraph', body:
                'Benjamin J. Schütz' },
            { heading: 'Commercial Register', kind: 'kv', body: [
                [ 'Court',           'Amtsgericht Lüneburg' ],
                [ 'Register Number', 'HRB 205600' ],
            ]},
            { heading: 'VAT Identification Number', kind: 'paragraph', body:
                'VAT Identification Number pursuant to Section 27a of the German VAT Act: DE 300040029.' },
            { heading: 'Responsible for Content (§ 18 para. 2 MStV)', kind: 'paragraph', body:
                'Benjamin J. Schütz, address as above.' },
            { heading: 'Liability for Content', kind: 'paragraphs', body: [
                'As a service provider, we are responsible for our own content on these pages under general laws pursuant to § 7 para. 1 TMG.',
                'However, pursuant to §§ 8–10 TMG, we are not obligated to monitor transmitted or stored third-party information or to investigate circumstances indicating illegal activity.',
                'Obligations to remove or block the use of information under general laws remain unaffected.',
            ]},
            { heading: 'Liability for Links', kind: 'paragraphs', body: [
                'Our website may contain links to external third-party websites. We have no influence on the content of those websites and therefore cannot assume any liability for such external content.',
                'The respective provider or operator of the linked sites is always responsible for the content of the linked pages.',
            ]},
            { heading: 'Copyright', kind: 'paragraphs', body: [
                'The content and works created by the site operator on these pages are subject to German copyright law.',
                'Reproduction, editing, distribution, or any kind of utilization outside the limits of copyright law requires the written consent of the respective author or creator.',
            ]},
        ],
    },

    privacy: {
        title: 'Data Privacy',
        eyebrow: 'How we handle your data',
        lastUpdated: 'Last updated 02 May 2026',
        intro: 'Puzzlemines is built to be played without an account. We collect as little data as possible and never sell or share it with advertisers.',
        sections: [
            { heading: '1. Who is responsible', kind: 'paragraph', body:
                'The data controller in the sense of the GDPR is aheadware GmbH, Wandelfeld 78, 21407 Deutsch Evern, Germany, represented by managing director Benjamin J. Schütz. You can reach us by phone at +49-4131-217 40 60 or by email at info@aheadware.com.' },
            { heading: '2. What we store on your device', kind: 'list', body: [
                'Your level progress, ranks, lives and gameplay stats — kept in your browser\'s local storage.',
                'Your settings (sound, music, stats opt-out, accepted cookie categories).',
                'A short, random session ID used to recover your save if you reload the page.',
            ]},
            { heading: '3. What we send to our servers', kind: 'paragraph', body:
                'Nothing personal. When you tap "Reset stats" or "Delete all data", the action stays on your device — there is no copy on our side to delete. Aggregated, anonymous play counts are only collected if you have enabled stats in Settings.' },
            { heading: '4. Cookies', kind: 'paragraph', body:
                'We use one strictly-necessary cookie to remember your consent choice and one optional analytics cookie that is only set after you opt in. You can change this any time under Settings → Cookie settings.' },
            { heading: '5. Your rights', kind: 'list', body: [
                'Right to access — request a copy of any data tied to you (typically: none).',
                'Right to rectification, erasure, and restriction of processing.',
                'Right to data portability (your local save is JSON-exportable on request).',
                'Right to lodge a complaint with your supervisory authority.',
            ]},
            { heading: '6. Contact for privacy questions', kind: 'paragraph', body:
                'For any privacy-related request, write to info@aheadware.com. We aim to respond within seven days.' },
        ],
    },

    tos: {
        title: 'Terms of Service',
        eyebrow: 'The agreement between us',
        lastUpdated: 'Last updated 02 May 2026',
        intro: 'By playing Puzzlemines you agree to these terms. They are written in plain language because legalese is no fun and we want you back tomorrow.',
        sections: [
            { heading: '1. Who can play', kind: 'paragraph', body:
                'Puzzlemines is a free puzzle game suitable for ages 7+. If you are under the age of consent in your country, please ask a parent or guardian before playing.' },
            { heading: '2. Your account & saves', kind: 'paragraph', body:
                'There is no account. Your progress lives on the device you play on. Clearing browser storage, reinstalling the app or switching devices will start you over — be kind to your future self.' },
            { heading: '3. Acceptable use', kind: 'list', body: [
                'Don\'t reverse-engineer or modify the game to cheat ranked levels or share modified builds.',
                'Don\'t use Puzzlemines to harass other players in any future social features we might add.',
                'Don\'t scrape level content for republication; you may share solutions and screenshots freely for personal, non-commercial use.',
            ]},
            { heading: '4. In-game coins', kind: 'paragraph', body:
                'Coins are earned through play and have no real-world value. They are not transferable, refundable or redeemable for cash, and we may rebalance their cost or rewards as the game evolves.' },
            { heading: '5. Updates & availability', kind: 'paragraph', body:
                'We patch bugs, add levels and tune difficulty. We try to never break your save, but in rare cases a major update may require it. We\'ll always tell you in the patch notes.' },
            { heading: '6. Liability', kind: 'paragraph', body:
                'Puzzlemines is provided "as is." To the extent permitted by law, we are not liable for indirect, incidental or consequential damages arising from your use of the game.' },
            { heading: '7. Changes to these terms', kind: 'paragraph', body:
                'When we update these terms we\'ll bump the "Last updated" date at the top of this page. Continued play after a change constitutes acceptance of the new terms.' },
            { heading: '8. Governing law', kind: 'paragraph', body:
                'These terms are governed by the laws of the Federal Republic of Germany, excluding the UN Convention on Contracts for the International Sale of Goods.' },
        ],
    },
};

function escape( s )
{
    return String( s ).replace( /[&<>"']/g, ch => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ ch ]
    ));
}

function sectionHtml( section )
{
    const { heading, kind, body } = section;
    let inner = '';
    if ( kind === 'paragraph' )
    {
        inner = `<p class="pm-legal__p">${ escape( body ) }</p>`;
    }
    else if ( kind === 'paragraphs' )
    {
        inner = body.map( p => `<p class="pm-legal__p">${ escape( p ) }</p>` ).join( '' );
    }
    else if ( kind === 'address' )
    {
        inner = `<address class="pm-legal__address">${
            body.map( line => `<span>${ escape( line ) }</span>` ).join( '' )
        }</address>`;
    }
    else if ( kind === 'list' )
    {
        inner = `<ul class="pm-legal__list">${
            body.map( item => `<li>${ escape( item ) }</li>` ).join( '' )
        }</ul>`;
    }
    else if ( kind === 'kv' )
    {
        inner = `<dl class="pm-legal__kv">${
            body.map( ([ k, v ]) =>
                `<div class="pm-legal__kvRow"><dt>${ escape( k ) }</dt><dd>${ escape( v ) }</dd></div>`
            ).join( '' )
        }</dl>`;
    }

    return `<section class="pm-legal__section">
        <h2 class="pm-legal__h2">${ escape( heading ) }</h2>
        ${ inner }
    </section>`;
}

export function renderLegalHtml( variant )
{
    const data = LEGAL_CONTENT[ variant ];
    if ( !data ) return '';

    return `
        <div class="pm-legal">
            <header class="pm-legal__top">
                <button class="pm-legal__back" id="pm-legal-back" type="button" aria-label="Back">${ BACK_SVG }</button>
                <div class="pm-legal__title">${ escape( data.title ) }</div>
                <div class="pm-legal__topSpacer" aria-hidden="true"></div>
            </header>

            <div class="pm-legal__scroll">
                <article class="pm-legal__article">
                    <div class="pm-legal__eyebrow">${ escape( data.eyebrow ) }</div>
                    <h1 class="pm-legal__h1">${ escape( data.title ) }</h1>
                    <div class="pm-legal__meta">${ escape( data.lastUpdated ) }</div>
                    <p class="pm-legal__intro">${ escape( data.intro ) }</p>

                    <div class="pm-legal__divider" aria-hidden="true"></div>

                    ${ data.sections.map( sectionHtml ).join( '' ) }

                    <footer class="pm-legal__footer">
                        <span>Puzzlemines</span> · ${ VERSION_LABEL } · ${ escape( data.lastUpdated ) }
                    </footer>
                </article>
            </div>
        </div>
    `;
}

export { LEGAL_CONTENT };
